/**
 * The planner (spec §6.3): map left, parameters right, figures and charts below. The planner
 * runs on every (debounced) change; save sends inputs and plan together; the KMZ downloads
 * only while nothing is unsaved.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Card, Flex, Group, Image, Modal, Radio, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { kmzUrl, useCreateMission, useDeleteMission, useMissionDrones, useUpdateMission, type Mission, type MissionDraft } from '@/api/missions';
import { FullBleed } from '@/components/FullBleed';
import { useFormat } from '@/components/useFormat';
import { usePageTitle } from '@/components/usePageTitle';
import { useDtm } from '@/dtm/useDtm';
import type { Basemap } from '@/map/layers';
import { plan as computePlan, type PlanError } from '@/planner';
import { footprint } from '@/planner/camera';
import { bufferedRing } from '@/planner/outline';
import { DEFAULT_INPUTS, type LngLat, type MissionInputs, type Plan } from '@/planner/types';
import { brand } from '@/theme/theme';
import { pointAlongPath } from './chartData';
import { listDjiMissions, supportsFolderSave, waypointFolder, writeDjiMission, type DirectoryHandleLike, type DjiMission } from './djiFolder';
import { stableStringify } from './compare';
import { ParameterPanel } from './ParameterPanel';
import { PlannerMap, type PlannerMapApi, type TerrainLayer } from './PlannerMap';
import type { EditorMode } from './polygonEditor';
import { composePreview, previewLine } from './preview';
import { missionSummary } from './summary';
import { ProfileChart } from './ProfileChart';
import { StatsList } from './StatsList';

const BASEMAP_KEY = 'forestum.missions.basemap';
const TERRAIN_KEY = 'forestum.missions.terrain';
const DJI_TARGET_KEY = 'forestum.missions.djiTarget';
type PlannerBasemap = Exclude<Basemap, 'none'>;
const DEFAULT_TERRAIN: TerrainLayer = { visible: true, opacity: 0.6 };

function readTerrain(): TerrainLayer {
  try {
    const v = JSON.parse(window.localStorage.getItem(TERRAIN_KEY) ?? '') as Partial<TerrainLayer>;
    const opacity = typeof v.opacity === 'number' && v.opacity >= 0.1 && v.opacity <= 1 ? v.opacity : DEFAULT_TERRAIN.opacity;
    return { visible: typeof v.visible === 'boolean' ? v.visible : DEFAULT_TERRAIN.visible, opacity };
  } catch {
    return DEFAULT_TERRAIN;
  }
}
function writeTerrain(v: TerrainLayer) {
  try {
    window.localStorage.setItem(TERRAIN_KEY, JSON.stringify(v));
  } catch {
    // A blocked store only loses the convenience.
  }
}

/** Which DJI Fly mission each Forestum mission last replaced, so the dialog preselects it. */
function readDjiTargets(): Record<string, string> {
  try {
    const v = JSON.parse(window.localStorage.getItem(DJI_TARGET_KEY) ?? '{}') as unknown;
    return v && typeof v === 'object' ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}
function writeDjiTarget(missionId: string, uuid: string) {
  try {
    window.localStorage.setItem(DJI_TARGET_KEY, JSON.stringify({ ...readDjiTargets(), [missionId]: uuid }));
  } catch {
    // A blocked store only loses the preselection.
  }
}

function readBasemap(): PlannerBasemap {
  try {
    const v = window.localStorage.getItem(BASEMAP_KEY);
    return v === 'ortho' ? 'ortho' : 'osm';
  } catch {
    return 'osm';
  }
}
function writeBasemap(v: PlannerBasemap) {
  try {
    window.localStorage.setItem(BASEMAP_KEY, v);
  } catch {
    // A blocked store only loses the convenience.
  }
}

export interface PlannerPageProps {
  /** A draft (no public_id) or absent for a new, unsaved mission. */
  mission?: Mission | MissionDraft;
}

export function PlannerPage({ mission }: PlannerPageProps) {
  const { t } = useTranslation('missions');
  const { number, dateTime } = useFormat();
  const navigate = useNavigate();
  const saved = mission && 'public_id' in mission ? mission : undefined;

  const { data: drones } = useMissionDrones();
  const [droneKey, setDroneKey] = useState(mission?.drone ?? 'air_3s');
  // Missions saved before an input existed get its default, both live and as the saved baseline.
  const savedInputs = useMemo(() => (mission ? { ...DEFAULT_INPUTS, ...mission.inputs } : null), [mission]);
  const [inputs, setInputs] = useState<MissionInputs>(savedInputs ?? DEFAULT_INPUTS);
  const [name, setName] = useState(mission?.name ?? '');
  const [notes, setNotes] = useState(mission?.notes ?? '');
  const [mode, setMode] = useState<EditorMode>(inputs.polygon.length ? 'view' : 'draw');
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);
  const [hoverPoint, setHoverPoint] = useState<LngLat | null>(null);
  const [basemap, setBasemap] = useState<PlannerBasemap>(readBasemap);
  const [terrain, setTerrain] = useState<TerrainLayer>(readTerrain);
  const [nameError, setNameError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  usePageTitle(name || t('page.untitled'));

  const drone = drones?.find((d) => d.key === droneKey) ?? drones?.[0];
  const [debounced] = useDebouncedValue(inputs, 150);
  const dtmPoints = useMemo(() => (debounced.takeoff ? [...debounced.polygon, debounced.takeoff] : debounced.polygon), [debounced.polygon, debounced.takeoff]);
  // The grid reaches the margin plus one line and one photo spacing beyond the polygon; a footprint width covers both.
  const dtmPad = drone ? debounced.margin_m + 2 * footprint(drone.camera, debounced.altitude_m).across_m + 50 : 50;
  const { dtm, isPending: dtmPending } = useDtm(dtmPoints, dtmPad);
  const terrainPending = inputs.terrain_follow && dtmPending;
  const { plan, planError } = useMemo((): { plan: Plan | null; planError: PlanError | null } => {
    if (!drone || debounced.polygon.length < 3) return { plan: null, planError: null };
    try {
      return { plan: computePlan(debounced, drone, dtm), planError: null };
    } catch (e) {
      const reason = e instanceof Error ? e.message : '';
      return { plan: null, planError: reason === 'spacing' || reason === 'too_large' ? reason : null };
    }
  }, [debounced, drone, dtm]);

  const fp = useMemo(() => (drone ? footprint(drone.camera, inputs.altitude_m) : null), [drone, inputs.altitude_m]);
  const marginRing = useMemo(() => (inputs.margin_m > 0 && inputs.polygon.length >= 3 ? bufferedRing(inputs.polygon, inputs.margin_m) : null), [inputs.polygon, inputs.margin_m]);

  // Compared on the debounced inputs the plan was computed from: dirty may flip on 150 ms late, never wrongly off.
  const current = useMemo(() => stableStringify({ name, notes, drone: droneKey, inputs: debounced, plan }), [name, notes, droneKey, debounced, plan]);
  const stored = useMemo(() => (saved ? stableStringify({ name: saved.name, notes: saved.notes, drone: saved.drone, inputs: savedInputs, plan: saved.plan }) : null), [saved, savedInputs]);
  const planChanged = useMemo(
    () => !!saved && !dtmPending && !!plan && stableStringify(plan) !== stableStringify(saved.plan) && stableStringify(debounced) === stableStringify(savedInputs),
    [saved, savedInputs, plan, debounced, dtmPending],
  );
  const dirty = !saved || terrainPending || current !== stored;
  const canDownload = !!saved && !dirty;

  // A selection outlives the plan it was made on only while the index still exists.
  const selected = selectedPhoto !== null && plan && selectedPhoto < plan.photos.length ? selectedPhoto : null;

  const create = useCreateMission();
  const update = useUpdateMission(saved?.public_id ?? '');
  const remove = useDeleteMission();

  const save = async () => {
    if (!name.trim()) {
      setNameError(t('page.nameRequired'));
      return;
    }
    if (!drone || terrainPending || inputs.polygon.length < 3) return;
    // The memoised plan lags the live inputs by the debounce, so recompute the pair that is sent.
    let plan: Plan;
    try {
      plan = computePlan(inputs, drone, dtm);
    } catch {
      return;
    }
    setNameError(null);
    try {
      if (saved) {
        await update.mutateAsync({ name, notes, drone: droneKey, inputs, plan });
        notifications.show({ message: t('page.saved'), color: 'forest' });
      } else {
        const m = await create.mutateAsync({ name, notes, drone: droneKey, inputs, plan });
        notifications.show({ message: t('page.saved'), color: 'forest' });
        await navigate({ to: '/staff/missions/$missionId', params: { missionId: m.public_id } });
      }
    } catch (e) {
      notifications.show({ message: errorMessage(e), color: 'red' });
    }
  };

  // The copy takes the live state, unsaved edits included; the original keeps its saved state.
  const duplicate = () =>
    void navigate({ to: '/staff/missions/new', state: { missionDraft: { name: t('page.copyName', { name }), notes, drone: droneKey, inputs } } });

  const del = async () => {
    if (!saved) return;
    try {
      await remove.mutateAsync(saved.public_id);
      await navigate({ to: '/staff/missions' });
    } catch (e) {
      notifications.show({ message: errorMessage(e), color: 'red' });
    }
  };

  const effectiveInterval = plan?.warnings.includes('speed_capped') ? plan.stats.photo_spacing_m / plan.speed_ms : null;

  // Saving into DJI Fly: pick the controller's folder (once), list its missions, replace the chosen one.
  const mapApi = useRef<PlannerMapApi | null>(null);
  const [dji, setDji] = useState<{ root: DirectoryHandleLike; missions: DjiMission[] } | null>(null);
  const [djiTarget, setDjiTarget] = useState<string | null>(null);
  const [djiBusy, setDjiBusy] = useState(false);
  const previewUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    if (typeof URL.createObjectURL !== 'function') return urls;
    for (const m of dji?.missions ?? []) if (m.preview) urls[m.uuid] = URL.createObjectURL(m.preview);
    return urls;
  }, [dji]);
  useEffect(() => () => Object.values(previewUrls).forEach((u) => URL.revokeObjectURL(u)), [previewUrls]);

  const reportDjiError = (e: unknown) => {
    // Cancelling the folder picker is an AbortError and not worth a message.
    if (!(e instanceof DOMException && e.name === 'AbortError')) notifications.show({ message: t('page.saveToDjiFailed', { reason: errorMessage(e) }), color: 'red' });
  };
  const openDji = async (changeFolder = false) => {
    if (!saved) return;
    setDjiBusy(true);
    try {
      const root = await waypointFolder(changeFolder);
      const missions = await listDjiMissions(root);
      const remembered = readDjiTargets()[saved.public_id];
      setDjiTarget(missions.some((m) => m.uuid === remembered) ? remembered : null);
      setDji({ root, missions });
    } catch (e) {
      reportDjiError(e);
    } finally {
      setDjiBusy(false);
    }
  };
  const overwriteDji = async () => {
    if (!saved || !dji || !djiTarget || !plan) return;
    setDjiBusy(true);
    try {
      const r = await fetch(kmzUrl(saved.public_id), { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const kmz = await r.blob();
      const canvas = await mapApi.current?.capture(inputs.polygon);
      const preview = canvas ? await composePreview(canvas, { title: name, line: previewLine(inputs, plan.speed_ms, plan.stats.photos, number, t('dji.photos')) }) : null;
      const summary = missionSummary({ name, droneName: drone?.name ?? droneKey, inputs, plan, missionId: saved.public_id, exportedAt: dateTime(new Date().toISOString()) }, t, number);
      const { path } = await writeDjiMission(dji.root, djiTarget, { kmz, name, summary, preview });
      writeDjiTarget(saved.public_id, djiTarget);
      const target = dji.missions.find((m) => m.uuid === djiTarget);
      notifications.show({ message: t('page.savedToDji', { name: target?.name ?? djiTarget, path }), color: 'forest' });
      setDji(null);
    } catch (e) {
      reportDjiError(e);
    } finally {
      setDjiBusy(false);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={1}>{name || t('page.untitled')}</Title>
        <Group gap="xs">
          {dirty && saved && (
            <Text fz={12} c={brand.textMuted}>
              {t('page.dirty')}
            </Text>
          )}
          {saved && (
            <Button variant="subtle" color="red" onClick={() => setDeleteOpen(true)}>
              {t('page.delete')}
            </Button>
          )}
          {saved && (
            <Button variant="default" onClick={duplicate}>
              {t('page.duplicate')}
            </Button>
          )}
          {supportsFolderSave() && (
            <Button variant="default" disabled={!canDownload} loading={djiBusy && !dji} title={canDownload ? t('page.saveToDjiHint') : t('page.unsavedHint')} onClick={() => void openDji()}>
              {t('page.saveToDji')}
            </Button>
          )}
          {canDownload ? (
            <Button component="a" href={kmzUrl(saved.public_id)} download variant="default">
              {t('page.download')}
            </Button>
          ) : (
            <Button variant="default" disabled title={t('page.unsavedHint')}>
              {t('page.download')}
            </Button>
          )}
          <Button onClick={() => void save()} loading={create.isPending || update.isPending} disabled={!plan || terrainPending}>
            {t('page.save')}
          </Button>
        </Group>
      </Group>
      {nameError && (
        <Text c="red" role="alert">
          {nameError}
        </Text>
      )}
      {planChanged && <Alert color="yellow">{t('page.planChanged')}</Alert>}
      {inputs.polygon.length < 3 && <Alert color="forest">{t('page.drawFirst')}</Alert>}
      {planError && <Alert color="red">{t(`page.planFailed.${planError}`)}</Alert>}
      {plan?.warnings.map((w) => (
        <Alert key={w} color="yellow">
          {t(`warnings.${w}`, { interval: effectiveInterval === null ? '' : number(effectiveInterval, 1) })}
        </Alert>
      ))}

      <FullBleed>
        <Stack gap="md">
      <Flex gap="md" direction={{ base: 'column', md: 'row' }} align="stretch">
        <Box style={{ flex: 1, minHeight: 480, height: 'calc(100vh - 260px)' }}>
          <PlannerMap
            polygon={inputs.polygon}
            plan={plan}
            takeoff={inputs.takeoff}
            mode={mode}
            onPolygonChange={(polygon) => setInputs((i) => ({ ...i, polygon }))}
            onTakeoffChange={(takeoff) => setInputs((i) => ({ ...i, takeoff }))}
            onModeChange={setMode}
            selectedPhoto={selected}
            onSelectPhoto={setSelectedPhoto}
            hoverPoint={hoverPoint}
            marginRing={marginRing}
            footprint={fp}
            basemap={basemap}
            onBasemapChange={(b) => {
              setBasemap(b);
              writeBasemap(b);
            }}
            dtm={dtm}
            terrain={terrain}
            onApi={(api) => {
              mapApi.current = api;
            }}
            onTerrainChange={(v) => {
              setTerrain(v);
              writeTerrain(v);
            }}
          />
        </Box>
        <Card w={{ base: '100%', md: 360 }} p="md" data-planner-panel>
          <ScrollArea h={{ base: 'auto', md: 'calc(100vh - 300px)' }} type="auto" offsetScrollbars>
            <Stack gap="lg">
            {plan && <StatsList plan={plan} dtmKnown={dtm !== null} />}
            <ParameterPanel
              inputs={inputs}
              onChange={(patch) => setInputs((i) => ({ ...i, ...patch }))}
              drones={drones ?? []}
              drone={droneKey}
              onDroneChange={setDroneKey}
              mode={mode}
              onModeChange={setMode}
              name={name}
              notes={notes}
              onNameChange={setName}
              onNotesChange={setNotes}
              dtmAvailable={dtm !== null}
              effectiveInterval={effectiveInterval}
              derivedSpeed={plan?.speed_ms ?? null}
              derivedInterval={plan && plan.photo_gaps_s.length ? Math.min(...plan.photo_gaps_s) : null}
              initialTab={saved ? 'flight' : 'area'}
            />
            </Stack>
          </ScrollArea>
        </Card>
      </Flex>

      {plan && <ProfileChart profile={plan.profile} onHover={(d) => setHoverPoint(d === null ? null : pointAlongPath(plan.waypoints, d))} />}
        </Stack>
      </FullBleed>

      <Modal opened={!!dji} onClose={() => setDji(null)} title={t('dji.title')} size="lg">
        {dji && dji.missions.length === 0 ? (
          <Text>{t('dji.empty')}</Text>
        ) : (
          <Stack gap="sm">
            <Text fz={13} c={brand.textMuted}>
              {t('dji.intro')}
            </Text>
            <Radio.Group value={djiTarget ?? ''} onChange={setDjiTarget}>
              <Stack gap="xs">
                {dji?.missions.map((m) => (
                  <Radio
                    key={m.uuid}
                    value={m.uuid}
                    label={
                      <Group gap="sm" wrap="nowrap">
                        {previewUrls[m.uuid] ? (
                          <Image src={previewUrls[m.uuid]} w={96} h={45} radius="sm" fit="cover" alt="" />
                        ) : (
                          <Box w={96} h={45} bg={brand.surface2} style={{ borderRadius: 4, flexShrink: 0 }} />
                        )}
                        <Box>
                          <Text fz={14} fw={500}>
                            {m.name ?? t('dji.unnamed')}
                          </Text>
                          <Text fz={12} c={brand.textMuted}>
                            {dateTime(new Date(m.modified).toISOString())}
                          </Text>
                        </Box>
                      </Group>
                    }
                  />
                ))}
              </Stack>
            </Radio.Group>
          </Stack>
        )}
        <Group justify="space-between" mt="md">
          <Button variant="subtle" onClick={() => void openDji(true)} disabled={djiBusy}>
            {t('dji.changeFolder')}
          </Button>
          <Group gap="xs">
            <Button variant="default" onClick={() => setDji(null)}>
              {t('dji.cancel')}
            </Button>
            {dji && dji.missions.length > 0 && (
              <Button color="red" disabled={!djiTarget} loading={djiBusy} onClick={() => void overwriteDji()}>
                {t('dji.overwrite')}
              </Button>
            )}
          </Group>
        </Group>
      </Modal>
      <Modal opened={deleteOpen} onClose={() => setDeleteOpen(false)} title={t('page.delete')}>
        <Stack>
          <Text>{t('page.deleteConfirm', { name })}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteOpen(false)}>
              {t('common:common.cancel')}
            </Button>
            <Button color="red" onClick={() => void del()} loading={remove.isPending}>
              {t('page.delete')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
